/**
 * InsiIdentityPanel — the confirmed-identity block shown at the top of the
 * create and edit dialogs. Composes the locked identity card with, in "matched"
 * mode (a fresh create / re-search), the birth-place auto-fill and the
 * "Status on save" chip. Both dialogs render this instead of repeating the
 * wiring (the source of the create/edit drift this consolidates).
 *
 *  - `matched`   : true for a fresh create/re-search → runs the auto-fill and
 *                  shows the status chip; false for an already-INS patient
 *                  opened for edit (locked card only, no overwrite, no chip).
 *  - `inseeCode` : INSi `lieuNaissance` driving the birth-place auto-fill.
 *  - `status`    : the reliability status to display (card colour + chip).
 */
import PropTypes from 'prop-types';
import { Box as MuiBox } from '@mui/material';

import LockedIdentityCard from './LockedIdentityCard';
import InsiBirthPlaceAutofill from './InsiBirthPlaceAutofill';
import InsiStatusOnSave from './InsiStatusOnSave';

export default function InsiIdentityPanel({
  name,
  dateNaissance,
  sexe,
  insIdentifier,
  birthPlace,
  birthPlaceInseeCode,
  status,
  matched,
  inseeCode,
  onUpdate,
}) {
  return (
    <MuiBox
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        marginBottom: 10,
      }}
    >
      {matched && <InsiBirthPlaceAutofill inseeCode={inseeCode} />}
      <LockedIdentityCard
        name={name}
        dateNaissance={dateNaissance}
        sexe={sexe}
        birthPlace={birthPlace}
        birthPlaceInseeCode={birthPlaceInseeCode}
        insIdentifier={insIdentifier}
        status={status}
        onUpdate={onUpdate}
      />
      {matched && <InsiStatusOnSave status={status} />}
    </MuiBox>
  );
}

InsiIdentityPanel.propTypes = {
  name: PropTypes.object.isRequired,
  dateNaissance: PropTypes.string,
  sexe: PropTypes.oneOf(['M', 'F']),
  insIdentifier: PropTypes.shape({
    value: PropTypes.string,
    type: PropTypes.string,
  }).isRequired,
  birthPlace: PropTypes.object,
  birthPlaceInseeCode: PropTypes.string,
  status: PropTypes.oneOf(['PROVISOIRE', 'RECUPEREE', 'QUALIFIEE']),
  /** Fresh create/re-search → auto-fill + status chip; false = edit-on-open. */
  matched: PropTypes.bool,
  inseeCode: PropTypes.string,
  /** Re-search handler; omit to hide "Update INSi Data" (no INSi access). */
  onUpdate: PropTypes.func,
};

InsiIdentityPanel.defaultProps = {
  dateNaissance: undefined,
  sexe: undefined,
  birthPlace: undefined,
  birthPlaceInseeCode: undefined,
  status: undefined,
  matched: false,
  inseeCode: undefined,
  onUpdate: undefined,
};
